/**
 * Finn erstatter (issue #147 F1, candidate addendum AI-01 to AI-14): who could take one shift, each
 * with what is known about them, then a preview of the one change, then the save. Pressing a person
 * writes nothing; only «Bruk …» does, and the page checks the plan again first (UC13). The manager
 * chooses: no automatic pick, no score, no "best". Beside the plan on a wide screen, in the page's
 * sheet on a phone. Presentation and the steps between them; the page owns the data and the write.
 */
import { T, useT } from '@/i18n';
import { ruleText } from '@/rules';
import { num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { useEffect, useRef, useState } from 'react';
import css from './CoverPanel.module.css';
import type { Availability, CoverCandidate, Tier } from './coverFlow';
import type { CaseOutcome } from './reviewBar.types';

/** What pressing «Bruk …» came to; `stale`: the plan changed since the preview, nothing was written. */
export type CoverResult = CaseOutcome | { kind: 'stale'; message: string };

export interface CoverPanelProps {
  /** "Tor 15.10, 15:00–23:00" */
  title: string;
  /** who holds the shift now; null for an open shift */
  holder: string | null;
  published: boolean;
  /** null while the plan around the shift is read */
  candidates: CoverCandidate[] | null;
  /** the read failed, or the shift is gone: said, with a retry */
  problem: string | null;
  onRetry: () => void;
  /** timesheet.view: hours may be said */
  showHours: boolean;
  /** may change the plan; without it the list is for reading */
  editable: boolean;
  /** the person previewed; the page marks the shift in the plan with it */
  pick: number | null;
  onPick: (userId: number | null) => void;
  onApply: (userId: number) => Promise<CoverResult>;
  onClose: () => void;
}

const TIER_TITLE: Record<Tier, string> = {
  eligible: 'Kan ta vakten',
  missing: 'Mangler opplysninger',
  review: 'Må vurderes',
  blocked: 'Kan ikke ta vakten',
};
const initials = (name: string) =>
  name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('');
const h = (n: number) => num(Math.round(n * 10) / 10);

function availabilityLine(a: Availability): string {
  const wait = 'pending' in a && a.pending ? `, ${T('venter på godkjenning')}` : '';
  if (a.kind === 'reported')
    return a.from === '00:00' && a.to >= '23:59'
      ? T('Meldt tilgjengelig hele dagen') + wait
      : T('Meldt tilgjengelig {from}–{to}', { from: a.from, to: a.to }) + wait;
  if (a.kind === 'partly') return T('Bare tilgjengelig {from}–{to}', { from: a.from, to: a.to }) + wait;
  if (a.kind === 'unavail') return T('Meldt ikke tilgjengelig');
  return T('Tilgjengelighet ikke registrert');
}

/** cover.ts's short words as sentences; a rule the change adds in the catalogue's own words. */
function conflictsOf(c: CoverCandidate, t: ReturnType<typeof useT>): string[] {
  const out: string[] = [];
  for (const w of c.why) {
    const rest = /^(.+) t hvile$/.exec(w);
    const only = /^kun (.+)$/.exec(w);
    if (w === 'fravær') out.push(T('Har registrert fravær'));
    else if (w === 'ikke tilgjengelig')
      continue; // said by the availability line
    else if (w === 'har vakt da') out.push(T('Har en annen vakt samtidig'));
    else if (only)
      continue; // said by the availability line
    else if (rest)
      out.push(T('Bare {h} t hvile mellom vaktene; arbeidsmiljøloven krever 11 t', { h: rest[1] ?? '' }));
    else if (w === 'over avtalt uke') out.push(T('Går over avtalt uke'));
    else if (w === 'mangler fødselsdato')
      out.push(T('Fødselsdato mangler, så reglene for unge kan ikke sjekkes'));
    else out.push(T(w));
  }
  for (const e of c.adds) out.push(ruleText(t, e));
  return out;
}

function hoursLine(c: CoverCandidate): string {
  return c.norm != null
    ? T('{from} → {to} t / avtalt {norm} t', { from: h(c.before), to: h(c.after), norm: h(c.norm) })
    : T('{from} → {to} t denne uka', { from: h(c.before), to: h(c.after) });
}

export function CoverPanel(p: CoverPanelProps) {
  const t = useT();
  const [all, setAll] = useState(false);
  const [query, setQuery] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<Extract<CaseOutcome, { kind: 'saved' }> | null>(null);
  const head = useRef<HTMLElement | null>(null);
  const stage = receipt ? 'receipt' : p.pick != null ? 'preview' : 'list';
  // a new step is a new heading to move to; the list keeps its place and its filter (AI-07)
  // biome-ignore lint/correctness/useExhaustiveDependencies: move focus when the step changes
  useEffect(() => {
    head.current?.focus({ preventScroll: true });
  }, [stage]);

  const list = p.candidates ?? [];
  const picked = p.pick != null ? list.find((c) => c.userId === p.pick) : undefined;
  const open = list.filter((c) => c.tier !== 'blocked');
  const q = query.trim().toLocaleLowerCase('nb');
  const shown = all ? list.filter((c) => !q || c.name.toLocaleLowerCase('nb').includes(q)) : open.slice(0, 3);
  const tiers = (['eligible', 'missing', 'review', 'blocked'] as Tier[])
    .map((tier) => ({ tier, people: shown.filter((c) => c.tier === tier) }))
    .filter((g) => g.people.length);

  const choose = (userId: number | null) => {
    if (busy) return;
    setAck(false);
    setError(null);
    setNotice(null);
    p.onPick(userId);
  };
  const apply = async () => {
    if (!picked || busy) return;
    setBusy(true);
    setError(null);
    let r: CoverResult;
    try {
      r = await p.onApply(picked.userId);
    } finally {
      setBusy(false);
    }
    if (r.kind === 'saved') setReceipt(r);
    else if (r.kind === 'stale') {
      // nothing was written; back to the people as they are now (UC13)
      setNotice(r.message);
      setAck(false);
      p.onPick(null);
    } else if (r.kind === 'refused') setError(r.message);
    else if (r.kind === 'partial') setError(`${r.text} ${r.message}`);
    else if (r.kind === 'unknown')
      setError(T('Vi vet ikke om endringen ble lagret. Planen leses på nytt før du gjør noe mer.'));
  };
  const undo = async () => {
    if (!receipt?.undo || busy) return;
    setBusy(true);
    try {
      const r = await receipt.undo();
      if (r.kind === 'saved') setReceipt({ ...r, undo: undefined });
      else
        setError(r.kind === 'refused' ? r.message : T('Vi vet ikke om endringen ble angret. Les på nytt.'));
    } finally {
      setBusy(false);
    }
  };

  const heading =
    stage === 'receipt'
      ? T('Endringen er lagret')
      : stage === 'preview'
        ? T('Foreslått endring')
        : T('Velg en erstatter');
  const sub =
    stage === 'list'
      ? `${p.title}, ${p.holder ? T('for {name}', { name: p.holder }) : T('ledig vakt')}`
      : stage === 'preview'
        ? T('Ikke lagret, ingen varsles')
        : p.title;

  return (
    <aside className={`vpanel ${css.panel}`} aria-labelledby="vcover-t" data-testid="vcover-panel">
      <div className="vphd">
        <div>
          <b id="vcover-t" ref={head} tabIndex={-1}>
            {heading}
          </b>
          <span>{sub}</span>
        </div>
        <button type="button" className="x" aria-label={T('Lukk')} onClick={p.onClose} disabled={busy}>
          <Icon name="close" />
        </button>
      </div>

      {stage === 'receipt' && receipt && (
        <div className={css.body}>
          <output className={css.receipt} data-testid="vcover-receipt">
            <Icon name="check" size={18} strokeWidth={1.7} />
            <span>
              <b>{receipt.text}</b>
              {receipt.detail && <small>{receipt.detail}</small>}
            </span>
          </output>
          {error && (
            <p className={css.error} role="alert">
              {error}
            </p>
          )}
          <div className={css.actions}>
            {receipt.undo && (
              <button type="button" className={css.secondary} onClick={undo} disabled={busy}>
                {T('Angre')}
              </button>
            )}
            <button type="button" className={css.secondary} onClick={p.onClose} disabled={busy}>
              {T('Lukk')}
            </button>
          </div>
        </div>
      )}

      {/* the list stays mounted under the preview, so Tilbake finds it as it was (AI-07) */}
      <div className={css.body} hidden={stage !== 'list'} data-testid="vcover-list">
        <p className={css.lead}>{T('Du velger. Ingen blir tildelt automatisk.')}</p>
        {notice && (
          <output className={css.notice} data-testid="vcover-stale">
            <Icon name="refresh" size={18} strokeWidth={1.7} />
            <span>{notice}</span>
          </output>
        )}
        {p.problem ? (
          <div className={css.error} role="alert">
            <span>{p.problem}</span>
            <button type="button" className={css.link} onClick={p.onRetry}>
              {T('Prøv igjen')}
            </button>
          </div>
        ) : p.candidates == null ? (
          <output className={css.muted} aria-busy="true">
            {T('Henter planen rundt vakten …')}
          </output>
        ) : (
          <>
            {all && (
              <label className={css.search}>
                <Icon name="search" size={18} strokeWidth={1.7} />
                <input
                  type="search"
                  value={query}
                  placeholder={T('Søk etter navn')}
                  aria-label={T('Søk etter navn')}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>
            )}
            {!list.length ? (
              <p className={css.muted}>{T('Ingen andre i planen å velge mellom.')}</p>
            ) : !shown.length ? (
              <p className={css.muted}>
                {q
                  ? T('Ingen treff på «{q}».', { q: query.trim() })
                  : T('Ingen kan ta vakten uten fravær, sperret tid eller en annen vakt i veien.')}
              </p>
            ) : (
              tiers.map((g) => (
                <section key={g.tier} className={css.tier} data-testid={`vcover-tier-${g.tier}`}>
                  <h4>
                    {T(TIER_TITLE[g.tier])} <span>{g.people.length}</span>
                  </h4>
                  <ul>
                    {g.people.map((c) => {
                      const conflicts = conflictsOf(c, t);
                      return (
                        <li key={c.userId} className={css.cand} data-testid="vcover-cand">
                          <span className={css.avatar} aria-hidden="true">
                            {initials(c.name)}
                          </span>
                          <div className={css.who}>
                            <b>{c.name}</b>
                            <span className={css[`avail_${c.availability.kind}`]}>
                              {availabilityLine(c.availability)}
                            </span>
                            {p.showHours && <span>{hoursLine(c)}</span>}
                            {conflicts.length ? (
                              <ul className={c.tier === 'blocked' ? css.blocks : css.warns}>
                                {conflicts.map((x) => (
                                  <li key={x}>
                                    <Icon
                                      name={c.tier === 'blocked' ? 'stop' : 'warn'}
                                      size={14}
                                      strokeWidth={1.7}
                                    />
                                    {x}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span>{T('Ingen konflikter funnet i planen')}</span>
                            )}
                            {c.tier !== 'blocked' && p.editable && (
                              <button
                                type="button"
                                className={css.secondary}
                                onClick={() => choose(c.userId)}
                                aria-label={T('Se endring med {name}', { name: c.name })}
                              >
                                {T('Se endring')} <span aria-hidden="true">→</span>
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))
            )}
            {!all && list.length > shown.length && (
              <button
                type="button"
                className={css.link}
                onClick={() => setAll(true)}
                data-testid="vcover-all"
              >
                {T('Vis alle {n} medarbeidere', { n: list.length })}
              </button>
            )}
            {!p.editable && <p className={css.muted}>{T('Du kan se listen, men ikke endre planen.')}</p>}
          </>
        )}
      </div>

      {stage === 'preview' && (
        <div className={css.body} data-testid="vcover-preview">
          <button type="button" className={css.link} onClick={() => choose(null)} disabled={busy}>
            ← {T('Tilbake til kandidater')}
          </button>
          {!picked ? (
            <p className={css.muted}>{T('Personen er ikke i listen lenger.')}</p>
          ) : (
            <>
              <h4 className={css.when}>{p.title}</h4>
              <p className={css.change}>
                {p.holder ?? T('Ledig')} → {picked.name}
              </p>
              {p.showHours && (
                <dl className={css.facts}>
                  <div>
                    <dt>{T('Ukestimer for {name}', { name: picked.name })}</dt>
                    <dd>
                      {h(picked.before)} → {h(picked.after)} t
                      {picked.norm != null ? ` / ${T('avtalt {n} t', { n: h(picked.norm) })}` : ''}
                    </dd>
                  </div>
                </dl>
              )}
              <p className={css[`avail_${picked.availability.kind}`]}>
                {availabilityLine(picked.availability)}
              </p>
              {picked.tier === 'blocked' ? (
                // a breach or someone who cannot work then: said, and no way to apply it here (R3)
                <div className={css.blockBox} data-testid="vcover-block" role="alert">
                  <Icon name="stop" size={18} strokeWidth={1.7} />
                  <div>
                    <b>{T('Kan ikke velges her')}</b>
                    <ul>
                      {conflictsOf(picked, t).map((x) => (
                        <li key={x}>{x}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : picked.tier === 'review' ? (
                <div className={css.warnBox} data-testid="vcover-warn">
                  <Icon name="warn" size={18} strokeWidth={1.7} />
                  <div>
                    <b>{T('Dette må du vurdere')}</b>
                    <ul>
                      {conflictsOf(picked, t).map((x) => (
                        <li key={x}>{x}</li>
                      ))}
                    </ul>
                    <label className={css.ack}>
                      <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                      {T('Jeg har sett varslene over og vil likevel gi vakten til {name}', {
                        name: picked.name,
                      })}
                    </label>
                  </div>
                </div>
              ) : picked.tier === 'missing' ? (
                <p className={css.notice}>
                  <Icon name="info" size={18} strokeWidth={1.7} />
                  <span>
                    {T(
                      '{name} har ikke registrert tilgjengelighet for disse timene. Spør før du bruker endringen.',
                      {
                        name: picked.name,
                      },
                    )}
                  </span>
                </p>
              ) : (
                <p className={css.ok}>
                  <Icon name="check" size={18} strokeWidth={1.7} />
                  {T('Ingen regelvarsler i planen for {name}', { name: picked.name })}
                </p>
              )}
              {picked.tier !== 'blocked' && (
                <p className={css.muted} data-testid="vcover-consequence">
                  {p.published
                    ? p.holder
                      ? T('Vakten er publisert. {old} og {new} får varsel når du bruker endringen.', {
                          old: p.holder,
                          new: picked.name,
                        })
                      : T('Vakten er publisert. {new} får varsel når du bruker endringen.', {
                          new: picked.name,
                        })
                    : T('Vakten er et utkast. Ingen får varsel før du publiserer.')}{' '}
                  {T('Planen og reglene sjekkes på nytt når du trykker.')}
                </p>
              )}
              {error && (
                <p className={css.error} role="alert" data-testid="vcover-error">
                  {error}
                </p>
              )}
              <div className={css.actions}>
                {picked.tier !== 'blocked' && (
                  <button
                    type="button"
                    className={css.primary}
                    onClick={apply}
                    disabled={busy || (picked.tier === 'review' && !ack)}
                    aria-busy={busy}
                  >
                    {busy
                      ? T('Sjekker og lagrer …')
                      : p.published
                        ? T('Bruk endringen')
                        : T('Bruk som utkast')}
                  </button>
                )}
                <button type="button" className={css.secondary} onClick={p.onClose} disabled={busy}>
                  {T('Avbryt')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </aside>
  );
}
